import { useState, useEffect } from 'react'
import { Building2, Users, Clock, ArrowRight } from 'lucide-react'
import { trelloService } from '../lib/trello'

interface Board {
  id: string
  name: string
  url: string
  members: any[]
  lastActivity?: string
  cardCount?: number
}

interface SprintBoardSelectorProps {
  onBoardSelect: (boardId: string) => void
}

export default function SprintBoardSelector({ onBoardSelect }: SprintBoardSelectorProps) {
  const [boards, setBoards] = useState<Board[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedBoard, setSelectedBoard] = useState<string | null>(null)

  useEffect(() => {
    fetchBoards()
  }, [])

  const fetchBoards = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Check if Trello is connected
      if (!trelloService.isConnected()) {
        setError('Please connect your Trello account first')
        setLoading(false)
        return
      }

      // Fetch user's boards
      const userBoards = await trelloService.getBoards()
      
      // Enhance board data with additional info
      const enhancedBoards = await Promise.all(
        userBoards.map(async (board) => {
          try {
            // Get board members
            const members = await trelloService.getBoardMembers(board.id)
            
            // Get board cards count
            const cards = await trelloService.getBoardCards(board.id)
            
            return {
              ...board,
              members,
              cardCount: cards.length,
              lastActivity: '2 hours ago' // This would be calculated from actual data
            }
          } catch (error) {
            console.error(`Error fetching details for board ${board.name} (${board.id}):`, error)
            return {
              ...board,
              members: [],
              cardCount: 0,
              lastActivity: 'Unknown'
            }
          }
        })
      )

      setBoards(enhancedBoards)
    } catch (error) {
      console.error('Error fetching boards:', error)
      setError('Failed to load your Trello boards. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleBoardSelect = (boardId: string) => {
    setSelectedBoard(boardId)
  }

  const handleContinue = () => {
    if (selectedBoard) {
      onBoardSelect(selectedBoard)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Loading Your Boards</h2>
          <p className="text-gray-600">Fetching your Trello boards...</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Unable to Load Boards</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchBoards}
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (boards.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">No Boards Found</h2>
          <p className="text-gray-600">
            You don't have any Trello boards yet. Create a board first to analyze sprints.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Select Trello Board</h2>
        <p className="text-gray-600">
          Choose the board containing your sprint data to analyze
        </p>
      </div>

      <div className="grid gap-4">
        {boards.map((board) => (
          <div
            key={board.id}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedBoard === board.id
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => handleBoardSelect(board.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{board.name}</h3>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span>{board.members.length} members</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{board.cardCount} cards</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {selectedBoard === board.id && (
                  <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
                <ArrowRight className={`w-4 h-4 ${
                  selectedBoard === board.id ? 'text-primary-500' : 'text-gray-400'
                }`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={handleContinue}
          disabled={!selectedBoard}
          className={`px-6 py-2 rounded-md font-medium ${
            selectedBoard
              ? 'bg-primary-500 text-white hover:bg-primary-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  )
} 